import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  RotateCcw,
  Play,
  Pause,
  SkipForward,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { formatTime } from "../../utils/timeUtils";
import { trackEvent } from "../../utils/ga";

interface ReadingControlsProps {
  readingError: string | null;
  readingProgress: {
    currentTime: number;
    duration: number;
    maxTimeReached: number;
  };
  isReadingPaused: boolean;
  readingPlaybackRate: number;
  canSkip: boolean;
  handleSkipBriefing: () => void;
  handleRetryBriefing: () => void;
  toggleReadingPause: () => void;
  seekReadingTo: (time: number) => void;
  setReadingPlaybackRate: (rate: number) => void;
  progressBgRef: React.RefObject<HTMLDivElement | null>;
  setIsDragging: (isDragging: boolean) => void;
  handleProgressAction: (clientX: number) => void;
  isDragging: boolean;
}

const ReadingControls: React.FC<ReadingControlsProps> = ({
  readingError,
  readingProgress,
  isReadingPaused,
  readingPlaybackRate,
  canSkip,
  handleSkipBriefing,
  handleRetryBriefing,
  toggleReadingPause,
  seekReadingTo,
  setReadingPlaybackRate,
  progressBgRef,
  setIsDragging,
  handleProgressAction,
  isDragging,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
  const { t } = useTranslation();

  React.useEffect(() => {
    setIsCollapsed(window.innerWidth <= 768);
  }, []);

  return (
    <div
      className={`btn-group reading-mode ${isCollapsed ? "collapsed" : ""} ${isReadingPaused ? "is-paused" : "is-reading"}`}
    >
      {readingError ? (
        <>
          <div className="status-indicator">
            <span
              className="status-dot"
              style={{ backgroundColor: "#ff4d4d" }}
            />
            <span style={{ color: "#ff4d4d" }}>{readingError}</span>
          </div>
          <div className="btn-actions">
            <button
              className="btn-toggle"
              onClick={handleRetryBriefing}
              title={t("retryBriefing")}
            >
              <RefreshCw size={20} />
            </button>
          </div>
        </>
      ) : (
        <>
          <button
            className="btn-expand-toggle"
            onClick={() => {
              trackEvent(
                "BriefingControls",
                isCollapsed ? "Expand" : "Collapse",
              );
              setIsCollapsed(!isCollapsed);
            }}
            title={isCollapsed ? t("expand") : t("collapse")}
            style={{ order: -1 }}
          >
            {isCollapsed ? (
              <ChevronLeft size={20} />
            ) : (
              <ChevronRight size={20} />
            )}
          </button>

          <div className="reading-controls-group navigation">
            {!isCollapsed && (
              <>
                <button
                  className="btn-skip"
                  onClick={() => {
                    trackEvent("BriefingControls", "Skip Back 10s");
                    seekReadingTo(readingProgress.currentTime - 10);
                  }}
                  disabled={readingProgress.currentTime <= 0}
                  title={t("skipBack10")}
                >
                  <RotateCcw size={14} />
                  <span className="skip-label">10</span>
                </button>
                {canSkip && (
                  <button
                    className="btn-skip"
                    onClick={() => {
                      trackEvent("BriefingControls", "Skip Briefing");
                      handleSkipBriefing();
                    }}
                    title={t("skipBriefing")}
                  >
                    <SkipForward size={14} fill="currentColor" />
                  </button>
                )}
              </>
            )}
            <button
              className={`btn-toggle ${isReadingPaused ? "resuming" : ""}`}
              onClick={() => {
                trackEvent(
                  "BriefingControls",
                  isReadingPaused ? "Resume Briefing" : "Pause Briefing",
                );
                toggleReadingPause();
              }}
              title={isReadingPaused ? t("resumeBriefing") : t("pauseBriefing")}
            >
              {isReadingPaused ? (
                <Play size={20} fill="currentColor" />
              ) : (
                <Pause size={20} fill="currentColor" />
              )}
            </button>
          </div>

          {!isCollapsed && (
            <div className="reading-controls-group progress">
              <div className="reading-progress-container">
                <div className="reading-time-info current">
                  {formatTime(readingProgress.currentTime)}
                </div>
                <div
                  ref={progressBgRef}
                  className="reading-progress-bar-bg"
                  onPointerDown={(e) => {
                    trackEvent("BriefingControls", "Start Scrubbing");
                    setIsDragging(true);
                    handleProgressAction(e.clientX);
                    (e.currentTarget as HTMLDivElement).setPointerCapture(
                      e.pointerId,
                    );
                  }}
                  onPointerMove={(e) => {
                    if (isDragging) {
                      handleProgressAction(e.clientX);
                    }
                  }}
                  onPointerUp={(e) => {
                    trackEvent("BriefingControls", "End Scrubbing");
                    setIsDragging(false);
                    (e.currentTarget as HTMLDivElement).releasePointerCapture(
                      e.pointerId,
                    );
                  }}
                >
                  <div
                    className="reading-progress-max"
                    style={{
                      width: `${(readingProgress.maxTimeReached / readingProgress.duration) * 100}%`,
                    }}
                  />
                  <div
                    className="reading-progress-current"
                    style={{
                      width: `${(readingProgress.currentTime / readingProgress.duration) * 100}%`,
                    }}
                  />
                </div>
                <div className="reading-time-info total">
                  {formatTime(readingProgress.duration)}
                </div>
              </div>
            </div>
          )}
          <div className="reading-controls-group settings">
            {!isCollapsed && (
              <div className="speed-controls">
                <button
                  className={`btn-speed ${readingPlaybackRate === 1 ? "active" : ""}`}
                  onClick={() => {
                    trackEvent("BriefingControls", "Set Speed", "1x");
                    setReadingPlaybackRate(1);
                  }}
                >
                  1x
                </button>
                <button
                  className={`btn-speed ${readingPlaybackRate === 1.5 ? "active" : ""}`}
                  onClick={() => {
                    trackEvent("BriefingControls", "Set Speed", "1.5x");
                    setReadingPlaybackRate(1.5);
                  }}
                >
                  1.5x
                </button>
                <button
                  className={`btn-speed ${readingPlaybackRate === 2 ? "active" : ""}`}
                  onClick={() => {
                    trackEvent("BriefingControls", "Set Speed", "2x");
                    setReadingPlaybackRate(2);
                  }}
                >
                  2x
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ReadingControls;
